import { Injectable } from '@angular/core';
import { combineQueries, Query } from '@datorama/akita';
import { PatientModel } from '@shared/models/rx-models/interfaces/patient-model';
import { Observable } from 'rxjs';
import { PatientState, PatientStore } from './patient-store';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { emptyGuid } from '@shared/models/consts';
import { patientSectionModeEnum } from '@modules/patient/models/patient-section-visibility.enum';
import { ShellQuery } from '@shared/store/shell/shell-query';
import type { Maybe } from '@/types/globals';

@Injectable({ providedIn: 'root' })
export class PatientQuery extends Query<PatientState> {
	patientState$: Observable<PatientState> = this.select(state => state);
	patient$: Observable<PatientModel | null> = this.select(state => state.patient);
	isPatientInConflict$: Observable<boolean | null> = this.select(state => state.isPatientInConflict);
	patientGuid$: Observable<string | null> = this.select(state => state.patient?.UID).pipe(
		map(patientGuid => this.mapPatientGuid(patientGuid))
	);
	isPatientCleared$: Observable<boolean> = this.select(state => state.isPatientCleared);
	patientSectionMode$: Observable<patientSectionModeEnum> = combineQueries([
		this.selectLoading(),
		this.patientGuid$,
		this.shellQuery.isNewAndNotClonedRx$
	]).pipe(
		map(([isPatientLoading, patientGuid, isNewRx]) => {
			return (isNewRx && !patientGuid) || !(isPatientLoading || patientGuid)
				? patientSectionModeEnum.InlinePatientApp
				: patientSectionModeEnum.ReadOnly;
		}),
		distinctUntilChanged()
	);

	get patient(): PatientModel | null {
		return this.getValue().patient;
	}

	get patientGuid(): string | null {
		return this.mapPatientGuid(this.getValue().patient?.UID);
	}

	get isPatientInConflict(): boolean | null {
		return this.getValue().isPatientInConflict;
	}

	get isPatientCleared(): boolean {
		return this.getValue().isPatientCleared;
	}

	get isPatientAppConfigured(): boolean {
		return this.getValue().isPatientAppConfigured;
	}

	constructor(
		store: PatientStore,
		private readonly shellQuery: ShellQuery
	) {
		super(store);
	}

	private mapPatientGuid(patientGuid: Maybe<string>): string | null {
		if (!patientGuid) {
			return null;
		}

		return patientGuid === emptyGuid ? null : patientGuid;
	}
}
